import type { ItemAssessmentScope } from "../../../../types/assessment.ts";
import type { CredentialNode } from "../../../../types/assessment/data/credential.ts";
import type { AssessmentNodes } from "../../../../types/assessment/data/index.ts";
import { Monitor } from "../../../../types/assessment/monitor.ts";

const credentialFixGenerator = (status: "disabled" | "enabled") => ({
  gcloud: {
    description: `${
      status === "disabled" ? "Re-enable" : "Disable"
    } these service-account keys by running these commands in Google Cloud Shell:`,
    type: "shell" as const,
    generate: (
      node: CredentialNode,
      { scope }: { scope: ItemAssessmentScope }
    ) => {
      // Note that credential key here is full key resource locator, not just key id
      const [account, _, key] = node.key.split("/").slice(6);
      return `gcloud iam service-accounts keys ${
        status === "disabled" ? "enable" : "disable"
      } \\
  '${key}' \\
  --iam-account='${account}' \\
  --project=${scope.id}`;
    },
  },
});

export const CredentialPresets = {
  "Unused Service-Account Keys (Last 40 days)": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["gcloud"],
    show: "credential",
    searchTerm:
      'credential={enabledKey:"true" last40:"unused"}->identity={type:"service-account" status:"active"}',
    label: "Unused service-account key (last 40 days) ",
    priority: "HIGH",
    cta: "This service-account key has have not been used in the last 40 days. Consider removing this key.",
    remediation: (action: string) =>
      `This service-account key has not been used in the last 40 days. P0 has automatically ${action} to disable this key.`,
    description:
      "Service-account keys that have not been used in the last 40 days.",
    fix: credentialFixGenerator("enabled"),
    revert: credentialFixGenerator("disabled"),
  }),
  "Unused Service-Account Keys (Last 365 days)": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["gcloud"],
    show: "credential",
    searchTerm:
      'credential={enabledKey:"true" last365:"unused"}->identity={type:"service-account" status:"active"}',
    label: "Unused service-account key (last 365 days)",
    priority: "HIGH",
    cta: "This service-account key has have not been used in the last 365 days. Consider removing this key.",
    remediation: (action: string) =>
      `This service-account key has not been used in the last 365 days. P0 has automatically ${action} to disable this key.`,
    description:
      "Service-account keys that have not been used in the last 365 days.",
    fix: credentialFixGenerator("enabled"),
    revert: credentialFixGenerator("disabled"),
  }),
  "Stale Service-Account Key": Monitor<AssessmentNodes, "credential">({
    scopes: ["gcloud"],
    show: "credential",
    searchTerm: 'credential:stale90:"true"->identity=type:"service-account"',
    label: "Stale service-account key",
    priority: "HIGH",
    cta: "This service-account key has not been rotated in the last 90 days. Google recommends rotating keys every 90 days.",
    description:
      "Service-account keys that have not been rotated in the last 90 days.",
    management: {
      description:
        "P0 will automatically rotate your service account keys every 90 days",
      inputPrompt:
        "Please enter the Google Secret Manager resource name for the secret containing keys for this service account",
    },
  }),
  "Existing Access Keys for Root User": Monitor<AssessmentNodes, "credential">({
    scopes: ["aws"],
    show: "credential",
    searchTerm:
      'credential={enabledKey:"true" type:"key"}->identity=label:"root"',
    label: "Existing access keys for root user",
    priority: "CRITICAL",
    cta: `The root user's IAM access keys exist. It is highly recommended to remove these keys due to"
    security best practices. Note: The user key shown is a placeholder. This monitor only alerts you that 
    at least one key exists—it does not reflect the actual key.`,
    description: "Root user IAM access keys that exist.",
  }),
  "Stale IAM User Access Key": Monitor<AssessmentNodes, "credential">({
    scopes: ["aws"],
    show: "credential",
    searchTerm: 'credential={stale90:"true" type:"key"}->identity:',
    label: "Stale access key",
    priority: "HIGH",
    cta: "This service-account key has not been rotated in the last 90 days. AWS recommends rotating keys every 90 days.",
    description:
      "IAM access keys that have not been rotated in the last 90 days.",
  }),
  "Unused IAM User Access Keys (Last 90 days)": Monitor<
    AssessmentNodes,
    "credential"
  >({
    scopes: ["aws"],
    show: "credential",
    searchTerm:
      'credential={enabledKey:"true" last90:"unused" type:"key"}->identity:',
    label: "Unused access key (last 90 days)",
    priority: "HIGH",
    cta: "This IAM user's access key has not been used in the last 90 days. It is recommended to remove unused keys.",
    description: "IAM access keys that have not been used in the last 90 days.",
  }),
  "Stale IAM User Password": Monitor<AssessmentNodes, "credential">({
    scopes: ["aws"],
    show: "credential",
    searchTerm: 'credential={type:"password" stale90:"true"}->identity:',
    label: "Stale IAM User password",
    priority: "MEDIUM",
    cta: "This IAM user's password has not been changed in 90+ days. AWS recommends regular password rotation.",
    description:
      "IAM passwords that have not been changed in the last 90 days.",
  }),
  "Unused IAM User Password": Monitor<AssessmentNodes, "credential">({
    scopes: ["aws"],
    show: "credential",
    searchTerm: 'credential={type:"password" last90:"unused"}->identity:',
    label: "Unused IAM User password",
    priority: "MEDIUM",
    cta: "This IAM user's password has not been used in 90+ days. It is recommended to remove this form of authentication.",
    description: "IAM passwords that have not been used in the last 90 days.",
  }),
};
